import React, { useState, useEffect } from 'react';
import { getData, postData, putData, deleteData } from '../helpers/ApiSettings';
import ReservationTable from '../components/Reservations/ReservationTable';
import AddReservationModal from '../components/Reservations/AddReservationModal';
import EditReservationModal from '../components/Reservations/EditReservationModal';

const WorkshopReservations = () => {
  const [reservations, setReservations] = useState([]);
  const [users, setUsers] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [editingReservation, setEditingReservation] = useState(null);

  const [newReservation, setNewReservation] = useState({
    user: '',
    workshop: '',
    selectedDates: [],
    resa_status: 'confirmed',
    total_price: 0,
    user_details: {
      name: '',
      email: '',
      phone: ''
    }
  });

  const statusLabels = {
    pending_payment: 'Paiement en attente',
    confirmed: 'Confirmée',
    cancelled: 'Annulée',
    completed: 'Terminée'
  };

  const statusColors = {
    pending_payment: 'warning',
    confirmed: 'success',
    cancelled: 'danger',
    completed: 'info'
  };

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const response = await getData('/workshop-reservations?sort[0]=createdAt:desc&populate[user]=*&populate[workshop]=*&populate[workshop_date]=*');
      setReservations(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des réservations:', error);
      alert('Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getData('/users?sort[0]=username:asc');
      setUsers(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    }
  };

  const fetchWorkshops = async () => {
    try {
      const response = await getData('/workshops?populate=dates&sort[0]=title:asc');
      setWorkshops(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des ateliers:', error);
    }
  };

  useEffect(() => {
    fetchReservations();
    fetchUsers();
    fetchWorkshops();
  }, []);

  const handleAddReservation = async (e) => {
    e.preventDefault();

    // Validation
    if (!newReservation.workshop || newReservation.selectedDates.length === 0) {
      alert('Veuillez sélectionner un atelier et au moins une date');
      return;
    }

    // If no user selected, check user_details are filled
    if (!newReservation.user) {
      if (!newReservation.user_details.name || !newReservation.user_details.email) {
        alert('Veuillez remplir le nom et l\'email pour les réservations sans compte utilisateur');
        return;
      }
    }

    try {
      // Create reservation
      const reservationData = {
        workshop: newReservation.workshop,
        resa_status: newReservation.resa_status,
        total_price: newReservation.total_price,
        user_details: newReservation.user_details
      };

      // Only add user if one is selected
      if (newReservation.user) {
        reservationData.user = newReservation.user;
      }

      const response = await postData('/workshop-reservations', { data: reservationData });
      const reservationId = response.data.data.id;

      // Link selected dates to this reservation
      for (let dateId of newReservation.selectedDates) {
        await putData(`/workshop-dates/${dateId}`, {
          data: {
            workshop_reservation: reservationId
          }
        });
      }

      // Reset form
      setNewReservation({
        user: '',
        workshop: '',
        selectedDates: [],
        resa_status: 'confirmed',
        total_price: 0,
        user_details: {
          name: '',
          email: '',
          phone: ''
        }
      });

      await fetchReservations();
      window.$('#addReservationModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      alert('Réservation créée avec succès');
    } catch (error) {
      console.error('Erreur lors de la création de la réservation:', error);
      alert('Erreur lors de la création de la réservation');
    }
  };

  const handleUpdateReservation = async (e) => {
    e.preventDefault();

    try {
      await putData(`/workshop-reservations/${editingReservation.documentId}`, {
        data: {
          resa_status: editingReservation.resa_status,
          total_price: editingReservation.total_price,
          user_details: editingReservation.user_details
        }
      });

      setEditingReservation(null);
      await fetchReservations();
      window.$('#editReservationModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      alert('Réservation mise à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteReservation = async (documentId, dates) => {
    if (!window.confirm('Supprimer cette réservation ?')) return;

    try {
      // Unlink dates first
      if (dates && dates.length > 0) {
        for (let date of dates) {
          if (date.documentId) {
            await putData(`/workshop-dates/${date.documentId}`, {
              data: {
                workshop_reservation: null
              }
            });
          }
        }
      }

      // Delete reservation
      await deleteData(`/workshop-reservations/${documentId}`);
      await fetchReservations();
      alert('Réservation supprimée');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleEditClick = (reservation) => {
    setEditingReservation(JSON.parse(JSON.stringify(reservation)));
    setTimeout(() => {
      window.$('#editReservationModal').modal('show');
    }, 100);
  };

  const filteredReservations = selectedStatus === 'all'
    ? reservations
    : reservations.filter(r => r.resa_status === selectedStatus);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Réservations d'ateliers</h2>
        <button
          className="btn btn-primary"
          onClick={() => window.$('#addReservationModal').modal('show')}
        >
          Créer une réservation
        </button>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex gap-2 flex-wrap">
            <button
              className={`btn btn-sm ${selectedStatus === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setSelectedStatus('all')}
            >
              Toutes ({reservations.length})
            </button>
            {Object.keys(statusLabels).map(status => (
              <button
                key={status}
                className={`btn btn-sm ${selectedStatus === status ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setSelectedStatus(status)}
              >
                {statusLabels[status]} ({reservations.filter(r => r.resa_status === status).length})
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border" role="status"></div>
            </div>
          ) : filteredReservations.length === 0 ? (
            <p className="text-center text-muted">Aucune réservation</p>
          ) : (
            <ReservationTable
              reservations={filteredReservations}
              statusLabels={statusLabels}
              statusColors={statusColors}
              onEdit={handleEditClick}
              onDelete={handleDeleteReservation}
            />
          )}
        </div>
      </div>

      <AddReservationModal
        newReservation={newReservation}
        setNewReservation={setNewReservation}
        onSubmit={handleAddReservation}
        users={users}
        workshops={workshops}
        statusLabels={statusLabels}
      />

      <EditReservationModal
        editingReservation={editingReservation}
        setEditingReservation={setEditingReservation}
        onSubmit={handleUpdateReservation}
        statusLabels={statusLabels}
      />
    </div>
  );
};

export default WorkshopReservations;
